import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import "./App.scss";
import store, { persistedStore } from "./redux";
import MainContainer from "./router";
import { PersistGate } from "redux-persist/integration/react";
import { SentryErrorBoundary } from "./sentry";

// Detect Android WebView and apply appropriate CSS class
// (used for the Android version of the Home Health mobile app).
const detectAndroidWebView = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isAndroid = userAgent.includes("android");
  const isWebView =
    /wv|android.+version\/[\d.]+/.test(userAgent) ||
    (window as any).AndroidAuth !== undefined;

  if (isAndroid && isWebView) {
    document.body.classList.add("android-webview");

    // Fix for Android WebView viewport measurement differences:
    // use VisualViewport API when available and expose a stable CSS var used by layout height calcs.
    const setAppHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty("--app-height", `${height}px`);
    };

    setAppHeight();
    window.visualViewport?.addEventListener("resize", setAppHeight);
    window.addEventListener("resize", setAppHeight);
  }
};

// Run detection immediately
detectAndroidWebView();

// Initialize PWA and Firebase FCM for ChromeOS only (not Electron/mobile/other browsers)
if (!window.electron && !window.circadia) {
  // Check if this is ChromeOS before initializing PWA features
  import("./util/platformDetection")
    .then(({ getPlatformInfo }) => {
      const platformInfo = getPlatformInfo();

      if (!platformInfo.isChromeOS) {
        return;
      }

      // Initialize service worker updates (disabled auto-reload to prevent interrupting user actions)
      import("./util/serviceWorkerUpdater")
        .then(({ startAutoUpdate }) => {
          // Check every 5 minutes, but DON'T auto-reload (prevents interrupting logout, forms, etc.)
          startAutoUpdate(5, false);
        })
        .catch(() => {
          // Service worker updater not available
        });

      // Dynamic import to initialize push notifications
      import("./util/webPushNotifications")
        .then(({ webPushManager, isChromeOSWebBrowser }) => {
          if (!isChromeOSWebBrowser()) {
            return;
          }

          // Set Firebase configuration if available
          const firebaseConfig = {
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: import.meta.env
              .VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: import.meta.env.VITE_FIREBASE_APP_ID,
          };

          const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

          // Only initialize if ALL required config values are present
          if (firebaseConfig.projectId && firebaseConfig.apiKey && vapidKey) {
            webPushManager.setFirebaseConfig(firebaseConfig, vapidKey);
          }
        })
        .catch(() => {
          // Firebase FCM not available
        });
    })
    .catch(() => {
      // Platform detection not available
    });
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <SentryErrorBoundary fallback={() => <div>An error has occurred</div>}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistedStore}>
        <MainContainer />
      </PersistGate>
    </Provider>
  </SentryErrorBoundary>
);
